import { useEffect, useState } from 'react';

/**
 * Mobile breakpoint in pixels - consistent across the app
 * This matches the common 768px breakpoint used throughout the application
 */
export const MOBILE_BREAKPOINT = 768;

/**
 * Detects if the current device is mobile based on window width
 */
export function isMobileDevice(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;
}

/**
 * Creates a media query listener for mobile detection
 */
export function createMobileMediaQuery(): MediaQueryList {
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
}

/**
 * React hook for mobile detection with proper cleanup
 * This is a general-purpose hook that can be used throughout the application
 */
export function useMobileDetection(): {
  isMobile: boolean;
  setIsMobile: (isMobile: boolean) => void;
} {
  const [isMobile, setIsMobile] = useState(() => isMobileDevice());

  useEffect(() => {
    const mediaQuery = createMobileMediaQuery();
    setIsMobile(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', handler);

    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, []);

  return { isMobile, setIsMobile };
}
